// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import useStore from "@/hooks/useStore";
import useTranslation from "@/hooks/useTranslation";
import type { TyApiResponse, TyCouponDiscount, TyCouponPreview, TySubscription, TySubscriptionConfig, TySubscriptionPeriod } from "@/types";
import CouponBadge from "@/UI/components/CouponBadge";
import CouponInput from "@/UI/inputs/CouponInput";
import { getMenuHost } from "@/utils";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

export type PurchaseBranchAddonPopupDetails = {
    onSuccess: () => void;
    existingAddon?: { branches: number; activeAmount: number; comingAmount: number };
};

type Props = {
    onClose: () => void;
    details?: PurchaseBranchAddonPopupDetails;
};

type AddonResult = {
    branchCount: number;
    totalAmount: number;
    couponDiscount?: number;
    coupon?: { code: string; discount: TyCouponDiscount } | null;
    paymentDueAt?: string | null;
    paymentReceivers: { method: string; name: string; phone: string }[];
    whatsappPhone: string;
};

type SubscriptionData = { current: TySubscription | null; pending: TySubscription | null; coming: TySubscription | null };

// --- Helpers ------------------------------------------------------------------------------------

const PERIOD_MONTHS: Record<TySubscriptionPeriod, number> = { monthly: 1, quarterly: 3, annually: 12 };

const WHATSAPP_SVG = (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.13-2.9-7.01A9.817 9.817 0 0 0 12.04 2zm.01 1.67c2.2 0 4.26.86 5.82 2.42a8.225 8.225 0 0 1 2.41 5.83c0 4.54-3.7 8.23-8.24 8.23-1.48 0-2.93-.39-4.19-1.15l-.3-.17-3.12.82.83-3.04-.2-.32a8.188 8.188 0 0 1-1.26-4.38c.01-4.54 3.7-8.24 8.25-8.24zM8.53 7.33c-.16 0-.43.06-.66.31-.22.25-.87.86-.87 2.07 0 1.22.89 2.39 1.01 2.56.13.17 1.75 2.67 4.25 3.73.59.27 1.05.43 1.41.55.59.19 1.13.16 1.56.1.48-.07 1.46-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.07-.1-.23-.16-.48-.27-.25-.14-1.47-.74-1.69-.82-.23-.08-.37-.12-.56.12-.16.25-.64.81-.78.97-.15.17-.29.19-.53.07-.26-.13-1.06-.39-2.02-1.25-.74-.66-1.23-1.48-1.38-1.73-.12-.24-.01-.39.11-.5.11-.11.27-.29.37-.44.13-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.11-.56-1.35-.77-1.84-.2-.48-.4-.42-.56-.43-.14 0-.3-.01-.47-.01z" />
    </svg>
);

function calcBreakdown(current: TySubscription, coming: TySubscription | null, config: TySubscriptionConfig, count: number) {
    const period = current.period;
    const months = PERIOD_MONTHS[period];
    const daysRemaining = Math.ceil((new Date(current.endDate!).getTime() - Date.now()) / 86_400_000);
    const remainingMonths = Math.min(Math.max(1, Math.ceil(daysRemaining / 30)), months);
    const monthlyRate = config.branchAddonPrices[period] / months;
    const activeAmount = Math.round(monthlyRate * remainingMonths * count);
    const comingAmount = coming ? config.branchAddonPrices[coming.period] * count : 0;
    return { activeAmount, comingAmount, totalAmount: activeAmount + comingAmount, remainingMonths };
}

// --- Sub-components -----------------------------------------------------------------------------

function PaymentMethodCard({ receiver, t }: { receiver: AddonResult["paymentReceivers"][number]; t: ReturnType<typeof useTranslation> }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(receiver.phone).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };
    return (
        <div className="bap-payment-method">
            <img src={`/images/${receiver.method}.png`} alt={receiver.name} className="bap-payment-method-icon" />
            <div className="bap-payment-method-info">
                <span className="bap-payment-method-name">{receiver.name}</span>
                <span className="bap-payment-method-phone">{receiver.phone}</span>
            </div>
            <button className={`bap-copy-btn${copied ? " bap-copy-btn--copied" : ""}`} onClick={handleCopy} type="button">
                {copied ? t.branchAddon.copied : t.branchAddon.copyPhone}
            </button>
        </div>
    );
}

function SlugCopyRow({ slug, t }: { slug: string; t: ReturnType<typeof useTranslation> }) {
    const [copied, setCopied] = useState(false);
    const fullUrl = getMenuHost(slug);
    const handleCopy = () => {
        navigator.clipboard.writeText(fullUrl).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };
    return (
        <div className="bap-slug-row">
            <div className="bap-slug-info">
                <span className="bap-slug-label">{t.branchAddon.storeSlugLabel}</span>
                <span className="bap-slug-note">{t.branchAddon.storeSlugNote}</span>
            </div>
            <div className="bap-slug-copy">
                <span className="bap-slug-value" dir="ltr">
                    {fullUrl}
                </span>
                <button className={`bap-copy-btn${copied ? " bap-copy-btn--copied" : ""}`} onClick={handleCopy} type="button">
                    {copied ? t.branchAddon.copied : t.branchAddon.copyPhone}
                </button>
            </div>
        </div>
    );
}

/** The WhatsApp message and the deadline notice are Arabic-only, matching the rest of this flow. */
function fmtArabicDate(date: Date): string {
    return date.toLocaleDateString("ar-EG", { day: "numeric", month: "long", year: "numeric" });
}

// --- Logic --------------------------------------------------------------------------------------

export default function PurchaseBranchAddonPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const store = useStore((s) => s.store);

    const { data: configData, loading: configLoading } = useFetch<TyApiResponse<TySubscriptionConfig>>("/subscriptions/config");
    const { data: subData, loading: subLoading } = useFetch<TyApiResponse<SubscriptionData>>("/subscriptions/current");

    const config = configData?.data ?? null;
    const current = subData?.data?.current ?? null;
    const coming = subData?.data?.coming ?? null;

    const isExistingAddon = !!details?.existingAddon;
    const isLoading = isExistingAddon ? configLoading : configLoading || subLoading;

    const [branchCount, setBranchCount] = useState(1);
    const [step, setStep] = useState<"input" | "payment">(isExistingAddon ? "payment" : "input");
    const [addonResult, setAddonResult] = useState<AddonResult | null>(null);
    const [coupon, setCoupon] = useState<TyCouponPreview | null>(null);

    // When opening for an existing addon, build AddonResult from config once it loads
    useEffect(() => {
        if (!isExistingAddon || !config || !details?.existingAddon) return;
        const { branches, activeAmount, comingAmount } = details.existingAddon;
        setAddonResult({
            branchCount: branches,
            totalAmount: activeAmount + comingAmount,
            paymentReceivers: config.paymentReceivers,
            whatsappPhone: config.whatsappPhone,
        });
    }, [config, isExistingAddon]);

    const {
        data: submitData,
        loading: submitting,
        error: submitError,
        invoke: submit,
    } = useFetch<TyApiResponse<AddonResult>>("/subscriptions/branch-addon", { method: "POST" }, false);

    useEffect(() => {
        if (!submitData?.success || !submitData.data) return;
        setAddonResult(submitData.data);
        setStep("payment");
        details?.onSuccess();
    }, [submitData]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        submit({ method: "POST", data: { branchCount, ...(coupon ? { coupon: coupon.code } : {}) } });
    };

    // --- Loading state --------------------------------------------------------------------------

    if (isLoading) {
        return (
            <div className="bap-popup">
                <div className="bap-header">
                    <p className="bap-title">{t.branchAddon.title}</p>
                    <button type="button" className="bap-close" onClick={onClose} aria-label="Close">
                        <img src="/icons/close-icon.svg" alt="" width={16} height={16} />
                    </button>
                </div>
                <div className="bap-loading">
                    <span className="bap-spinner" />
                </div>
            </div>
        );
    }

    // When opening for an existing addon, block render until addonResult is ready
    if (isExistingAddon && !addonResult) {
        return (
            <div className="bap-popup">
                <div className="bap-header">
                    <p className="bap-title">{t.branchAddon.title}</p>
                    <button type="button" className="bap-close" onClick={onClose} aria-label="Close">
                        <img src="/icons/close-icon.svg" alt="" width={16} height={16} />
                    </button>
                </div>
                <div className="bap-loading">
                    <span className="bap-spinner" />
                </div>
            </div>
        );
    }

    // --- No active subscription (new addon flow only) ------------------------------------------

    if (!isExistingAddon && (!config || !current || !current.endDate)) {
        return (
            <div className="bap-popup">
                <div className="bap-header">
                    <p className="bap-title">{t.branchAddon.title}</p>
                    <button type="button" className="bap-close" onClick={onClose} aria-label="Close">
                        <img src="/icons/close-icon.svg" alt="" width={16} height={16} />
                    </button>
                </div>
                <div className="bap-loading">
                    <p className="bap-no-sub-note">{t.branchAddon.subtitle}</p>
                </div>
            </div>
        );
    }

    const PERIOD_LABELS: Record<TySubscriptionPeriod, string> = {
        monthly: t.subscriptions.periodMonthly,
        quarterly: t.subscriptions.periodQuarterly,
        annually: t.subscriptions.periodAnnually,
    };

    // --- Payment step ---------------------------------------------------------------------------

    if (step === "payment" && addonResult) {
        const {
            totalAmount,
            paymentReceivers,
            whatsappPhone,
            branchCount: count,
            paymentDueAt,
            coupon: applied,
            couponDiscount: saved,
        } = addonResult;
        const slug = store?.slug ?? "";
        const waPhone = whatsappPhone.replace("+", "");
        const requestedOn = fmtArabicDate(new Date());
        const waMessage = encodeURIComponent(
            `طلب إضافة فروع - منيوكيت\nرابط المتجر: ${slug}\nتاريخ الطلب: ${requestedOn}\nعدد الفروع: ${count} فرع\nالمبلغ: ${totalAmount} ${t.branchAddon.egp}`
        );
        const waUrl = `https://wa.me/${waPhone}?text=${waMessage}`;

        return (
            <div className="bap-popup">
                <div className="bap-header">
                    <p className="bap-title">{t.branchAddon.title}</p>
                    <button type="button" className="bap-close" onClick={onClose} aria-label="Close">
                        <img src="/icons/close-icon.svg" alt="" width={16} height={16} />
                    </button>
                </div>

                <div className="bap-body">
                    <div className="bap-amount-card">
                        <div className="bap-amount-hero">
                            <span className="bap-amount-number">{totalAmount.toLocaleString()}</span>
                            <span className="bap-amount-egp">{t.branchAddon.egp}</span>
                        </div>
                        <p className="bap-amount-label">{t.branchAddon.amountLabel}</p>
                    </div>

                    {applied && <CouponBadge code={applied.code} discount={applied.discount} savedAmount={saved ?? 0} compact />}

                    {paymentDueAt && (
                        <p className="bap-pay-window bap-pay-window--notice">
                            {t.subscriptions.payWindowNote.replace("{date}", fmtArabicDate(new Date(paymentDueAt)))}
                        </p>
                    )}

                    {slug && <SlugCopyRow slug={slug} t={t} />}

                    <p className="bap-section-label">{t.branchAddon.paymentSubtitle}</p>

                    <div className="bap-payment-methods">
                        {paymentReceivers.map((r) => (
                            <PaymentMethodCard key={r.method} receiver={r} t={t} />
                        ))}
                    </div>

                    <a className="bap-whatsapp-btn" href={waUrl} target="_blank" rel="noopener noreferrer">
                        {WHATSAPP_SVG}
                        {t.branchAddon.sendReceiptButton}
                    </a>

                    <p className="bap-success-note">{t.branchAddon.successNote}</p>
                </div>
            </div>
        );
    }

    // --- Input step -----------------------------------------------------------------------------

    const { activeAmount, comingAmount, totalAmount } = calcBreakdown(current!, coming, config!, branchCount);
    const couponDiscount = coupon?.discountAmount ?? 0;
    const payableTotal = totalAmount - couponDiscount;

    return (
        <div className="bap-popup">
            <div className="bap-header">
                <p className="bap-title">{t.branchAddon.title}</p>
                <button type="button" className="bap-close" onClick={onClose} aria-label="Close">
                    <img src="/icons/close-icon.svg" alt="" width={16} height={16} />
                </button>
            </div>

            <form className="bap-body" onSubmit={handleSubmit} noValidate>
                {/* Current plan card */}
                <div className="bap-plan-card">
                    <div className="bap-plan-badge-row">
                        <span className="bap-status-badge">{t.subscriptions.statusActive}</span>
                        <span className="bap-period-badge">{PERIOD_LABELS[current!.period]}</span>
                    </div>
                    <div className="bap-plan-divider" />
                    <div className="bap-plan-meta">
                        <div className="bap-plan-meta-item">
                            <span className="bap-plan-meta-label">{t.subscriptions.periodLabel}</span>
                            <span className="bap-plan-meta-value">{PERIOD_LABELS[current!.period]}</span>
                        </div>
                        <div className="bap-plan-meta-sep" />
                        <div className="bap-plan-meta-item">
                            <span className="bap-plan-meta-label">{t.subscriptions.branchesLabel}</span>
                            <span className="bap-plan-meta-value">
                                {current!.branches} {t.subscriptions.branchCount}
                            </span>
                        </div>
                        <div className="bap-plan-meta-sep" />
                        <div className="bap-plan-meta-item">
                            <span className="bap-plan-meta-label">{t.branchAddon.endsLabel}</span>
                            <span className="bap-plan-meta-value" dir="ltr">
                                {new Date(current!.endDate!).toLocaleDateString("en-GB", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                })}
                            </span>
                        </div>
                    </div>
                    {coming && (
                        <div className="bap-coming-note">
                            <span className="bap-coming-note-label">{t.branchAddon.comingPlanLabel}</span>
                            <span className="bap-coming-note-value">{PERIOD_LABELS[coming.period]}</span>
                        </div>
                    )}
                </div>

                {/* Branch count picker */}
                <div className="bap-count-section">
                    <span className="bap-count-label">{t.branchAddon.branchCountLabel}</span>
                    <div className="bap-counter">
                        <button
                            type="button"
                            className="bap-counter-btn"
                            onClick={() => setBranchCount((c) => Math.max(1, c - 1))}
                            disabled={branchCount <= 1}
                        >
                            −
                        </button>
                        <span className="bap-counter-value">{branchCount}</span>
                        <button type="button" className="bap-counter-btn" onClick={() => setBranchCount((c) => c + 1)}>
                            +
                        </button>
                    </div>
                </div>

                {/* Price breakdown */}
                <div className="bap-breakdown">
                    <div className="bap-breakdown-row">
                        <span className="bap-breakdown-label">{t.branchAddon.activeAmountLabel}</span>
                        <span className="bap-breakdown-value">
                            {activeAmount.toLocaleString()} {t.branchAddon.egp}
                        </span>
                    </div>
                    {coming && (
                        <div className="bap-breakdown-row">
                            <span className="bap-breakdown-label">{t.branchAddon.comingAmountLabel}</span>
                            <span className="bap-breakdown-value">
                                {comingAmount.toLocaleString()} {t.branchAddon.egp}
                            </span>
                        </div>
                    )}
                    {couponDiscount > 0 && (
                        <div className="bap-breakdown-row">
                            <span className="bap-breakdown-label">{t.subscriptions.couponDiscountLabel}</span>
                            <span className="bap-breakdown-value bap-breakdown-value--discount">
                                −{couponDiscount.toLocaleString()} {t.branchAddon.egp}
                            </span>
                        </div>
                    )}
                    <div className="bap-breakdown-divider" />
                    <div className="bap-breakdown-total">
                        <span className="bap-breakdown-total-label">{t.branchAddon.totalLabel}</span>
                        <span className="bap-breakdown-total-value">
                            {payableTotal.toLocaleString()}
                            <span className="bap-breakdown-total-egp"> {t.branchAddon.egp}</span>
                        </span>
                    </div>
                </div>

                <CouponInput action="branch" branches={branchCount} applied={coupon} onApply={setCoupon} disabled={submitting} />

                <p className="bap-pay-window">{t.subscriptions.payWindowTitle}</p>

                {submitError && <p className="bap-error">{submitError}</p>}

                <button type="submit" className="bap-submit" disabled={submitting}>
                    {submitting ? <span className="bap-spinner" /> : t.branchAddon.submitButton}
                </button>
            </form>
        </div>
    );
}
